import React, { useEffect, useMemo, useState } from 'react';
import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { API_BASE } from '../api';

const ImageOcrViewer = ({ src, maxWidth = 720 }) => {
  const [ocrData, setOcrData] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrError, setOcrError] = useState('');
  const [imgSize, setImgSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!src) {
      return;
    }
    setOcrData(null);
    setOcrLoading(true);
    setOcrError('');

    const img = new Image();
    img.onload = async () => {
      setImgSize({ width: img.width, height: img.height });
      try {
        const response = await fetch(`${API_BASE}/image-ocr?url=${encodeURIComponent(src)}`);
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.error || 'Falha ao gerar OCR.');
        }
        setOcrData(data);
      } catch (error) {
        setOcrError(error.message || 'Falha ao gerar OCR.');
      } finally {
        setOcrLoading(false);
      }
    };
    img.onerror = () => {
      setOcrLoading(false);
      setOcrError('Falha ao carregar imagem.');
    };
    img.src = src;
  }, [src]);

  const scale = useMemo(() => {
    if (!imgSize.width || !maxWidth) {
      return 1;
    }
    return Math.min(1, maxWidth / imgSize.width);
  }, [imgSize.width, maxWidth]);

  const renderWidth = imgSize.width * scale;
  const renderHeight = imgSize.height * scale;

  return (
    <Box position="relative" maxW={`${maxWidth}px`}>
      <img
        src={src}
        alt="Documento"
        style={{
          width: renderWidth ? `${renderWidth}px` : '100%',
          height: renderHeight ? `${renderHeight}px` : 'auto',
          display: 'block'
        }}
      />

      {ocrLoading ? (
        <Flex
          position="absolute"
          inset={0}
          align="center"
          justify="center"
          bg="rgba(255,255,255,0.7)"
          zIndex={2}
        >
          <Spinner size="sm" color="gray.500" mr={2} />
          <Text fontSize="sm" color="gray.600">Preparando OCR...</Text>
        </Flex>
      ) : null}

      {ocrError ? (
        <Flex
          position="absolute"
          inset={0}
          align="center"
          justify="center"
          bg="rgba(255,255,255,0.8)"
          zIndex={2}
        >
          <Text fontSize="sm" color="red.500">{ocrError}</Text>
        </Flex>
      ) : null}

      {ocrData && ocrData.words ? (
        <Box
          className="igm-ocr-layer"
          position="absolute"
          left={0}
          top={0}
          width={`${renderWidth}px`}
          height={`${renderHeight}px`}
          zIndex={1}
        >
          {ocrData.words.map((word, index) => {
            const left = word.x * scale;
            const top = word.y * scale;
            const width = word.w * scale;
            const height = word.h * scale;
            return (
              <span
                key={`${word.text}-${index}`}
                className="igm-ocr-word"
                style={{
                  left: `${left}px`,
                  top: `${top}px`,
                  width: `${width}px`,
                  height: `${height}px`,
                  fontSize: `${Math.max(6, height)}px`
                }}
              >
                {word.text}
              </span>
            );
          })}
        </Box>
      ) : null}
    </Box>
  );
};

export default ImageOcrViewer;
